import { useMutation } from "@apollo/client";
import {
  Button,
  HStack,
  Spacer,
  Stack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import ObjectID from "bson-objectid";
import { EditMeMutation, EditMeMutationVariables } from "gql/graphql";
import {
  BellOutlineIcon,
  CalendarOutlineIcon,
  PencilOutlineIcon,
} from "modules/icons";
import ArrowIcon from "modules/icons/arrow-icon";
import NotesIcon from "modules/icons/notes-icon";
import { useAuth } from "modules/providers/auth-provider";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import moment from "moment";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import {
  MIXPANEL_EVENT_TYPE,
  useMixpanel,
} from "../providers/mixpanel-provider/mixpanel-provider";
import { isUserOutOfOffice } from "modules/utils/Functions";
import EDIT_ME_MUTATION from "modules/hooks/_networking/edit-me";

const OutOfOfficeSettings = () => {
  const { me } = useAuth();
  const { openModal } = useOverlay();
  const { track } = useMixpanel();

  const outOfOffice = me?.outOfOffice;

  const isToggleOn = useMemo(() => {
    if (!me || !outOfOffice) return false;
    return (
      isUserOutOfOffice(me) || moment(outOfOffice.startTime).isAfter(moment())
    );
  }, [me, outOfOffice]);

  const [editMeMutationAPI] = useMutation<
    EditMeMutation,
    EditMeMutationVariables
  >(EDIT_ME_MUTATION);

  const openOutOfOfficeModal = useCallback(() => {
    if (!me) return;
    openModal({
      type: MODAL_TYPE.OUT_OF_OFFICE,
    });
  }, [me, openModal]);

  const deleteOutOfOffice = useCallback(async () => {
    if (!me) return;
    await editMeMutationAPI({
      variables: {
        input: {
          outOfOffice: {
            startTime: null,
            endTime: null,
            message: outOfOffice ? outOfOffice.message : null,
            isMuteNotifications: true,
          },
        },
      },
      optimisticResponse: {
        editMe: {
          ...me,
          outOfOffice: {
            id: outOfOffice ? outOfOffice.id : new ObjectID().toHexString(),
            startTime: null,
            endTime: null,
            message: outOfOffice ? outOfOffice.message : null,
            isMuteNotifications: true,
            __typename: "OutOfOffice",
          },
        },
      },
    });
    track(MIXPANEL_EVENT_TYPE.OUT_OF_OFFICE_SET, {
      "Out of Office Set": false,
    });
  }, [editMeMutationAPI, me, outOfOffice, track]);

  const onSwitchToggled = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!me) return;
      if (event.target.checked) {
        //https://teamcake.atlassian.net/browse/SYN-2167
        //wipe out any previous data except for the message
        await deleteOutOfOffice();
        openOutOfOfficeModal();
      } else {
        deleteOutOfOffice();
      }
    },
    [deleteOutOfOffice, me, openOutOfOfficeModal],
  );

  return (
    <Stack color={colors.gray50} spacing={1} w="full">
      <HStack w="full">
        <Text fontWeight={700} fontSize={14} color={colors.blackSecondary}>
          Out of Office Mode
        </Text>
        <Spacer />
        <Switch size="sm" isChecked={isToggleOn} onChange={onSwitchToggled} />
      </HStack>
      <Text fontSize={14} fontWeight={400} color={colors.gray50}>
        Show status as “Away” and add an auto-reply
      </Text>
      {isToggleOn && outOfOffice && (
        <VStack
          bg={colors.whiteTertiary}
          borderRadius={4}
          w="full"
          p={2}
          alignItems="flex-start"
        >
          <HStack w="full">
            <CalendarOutlineIcon fill={colors.gray30} />
            <Text fontSize={14} fontWeight={700} color={colors.gray50}>
              {moment(outOfOffice.startTime).format("MMM D, YYYY")}
            </Text>
            {outOfOffice.endTime && (
              <>
                <ArrowIcon
                  boxSize={3}
                  fill={colors.grayMain}
                  transform="rotate(180deg)"
                />
                <Text fontSize={14} fontWeight={700} color={colors.gray50}>
                  {moment(outOfOffice.endTime).format("MMM D, YYYY")}
                </Text>
              </>
            )}
          </HStack>
          {outOfOffice.message && (
            <HStack w="full" alignItems="flex-start">
              <NotesIcon boxSize={4} fill={colors.gray30} mt={1} />
              <Text
                fontSize={14}
                fontWeight={500}
                color={colors.gray50}
                wordBreak="break-all"
              >
                {outOfOffice.message}
              </Text>
            </HStack>
          )}
          {!outOfOffice.isMuteNotifications && (
            <HStack w="full" alignItems="flex-start">
              <BellOutlineIcon boxSize={4} fill={colors.gray30} mt={1} />
              <Text fontSize={14} fontWeight={500} color={colors.gray50}>
                Notifications On
              </Text>
            </HStack>
          )}
          <Button
            fontSize={12}
            color={colors.blue}
            fontWeight={700}
            leftIcon={<PencilOutlineIcon boxSize={4} fill={colors.blue} />}
            iconSpacing={1}
            variant="link"
            onClick={openOutOfOfficeModal}
          >
            Edit Details
          </Button>
        </VStack>
      )}
    </Stack>
  );
};

export default memo(OutOfOfficeSettings);
